/**
 * CBOR serialization — deterministic encoding per RFC 8949 Section 4.2.
 * Used at serialization boundaries only (presentationHeader, wire format).
 * String-keyed maps, no IANA-registered tags.
 *
 * Keys are sorted by UTF-16 code-unit order (JavaScript's default string
 * comparison). This is equivalent to RFC 8949 §4.2.1 length-first bytewise
 * order for ASCII keys, which covers all OwnYou map keys. Non-ASCII keys
 * would require explicit CBOR-encoded-length comparison.
 *
 * @traces §2.4.2, §7.5
 */
import { Encoder, decode } from 'cbor-x';
// PR #47 live smoke 2026-05-14: cbor-x@1.6.4 encodes Uint8Array with CBOR tag
// 64 (Uint8Array typed array, RFC 8949) IFF `hasNodeBuffer` is true at module
// load OR the encoder is constructed with `tagUint8Array: true`. In a Node
// process the default is "tag", in a browser/Service-Worker bundle the
// default is "no tag" — same `cbor-x@1.6.4` source, different runtime
// behaviour. The wallet (running in the extension SW) was encoding Uint8Array
// WITHOUT the tag while every verifier (DSP, publisher-sdk tests, Node) was
// encoding WITH the tag, so the DLEQ context digests never matched and every
// wallet-minted DisclosureGrant failed `verifyGrantDLEQ`. Force the option
// explicitly so the encoding is identical in every environment.
//
// Reference: `node_modules/cbor-x/encode.js:1099-1107`.
const encoder = new Encoder({
    useRecords: false,
    structuredClone: false,
    mapsAsObjects: true,
    tagUint8Array: true,
});
function sortKeys(value) {
    if (value === null || typeof value !== 'object')
        return value;
    if (value instanceof Uint8Array || ArrayBuffer.isView(value))
        return value;
    if (Array.isArray(value))
        return value.map(sortKeys);
    const obj = value;
    const sorted = {};
    for (const key of Object.keys(obj).sort()) {
        sorted[key] = sortKeys(obj[key]);
    }
    return sorted;
}
export function cborEncode(value) {
    return new Uint8Array(encoder.encode(sortKeys(value)));
}
export function cborDecode(data) {
    return decode(data);
}
